'use client'
import { localItemOptionType } from '@/app/ui/parts/default/sp/item/option'
import { colorType } from '@/lib/color/type'
import { colorSort } from '@/lib/color/util'
import { sizeType } from '@/lib/size/type'
import { sizeSort } from '@/lib/size/util'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { ItemOption } from '../../common/itemOption'

type localColorType = colorType
type localSizeType = sizeType

type ItemOptionSelectProps = {
  itemOptions: localItemOptionType[]
  selectedOption: localItemOptionType
  onChanged: (option: localItemOptionType) => void
  onSelecting: (selecting: boolean) => void
}

// selectedOptions: 選択済みオプションの配列、nullを含まないことを保証する
// onChanged: selectedOptions の変更を受け取るコールバック
// onSelecting: オプション選択中かどうかを表すフラグ。選択中は購入ボタンを無効化する（このコンポーネントの外で）
export const ItemOptionSelectSlider: React.FC<ItemOptionSelectProps> = ({
  itemOptions,
  selectedOption,
  onChanged,
}) => {
  // オプションのデータによってUIを分岐する
  const hasDefectiveData = useMemo(() => {
    const baseSizes: sizeType[] = []
    const baseColors: colorType[] = []
    const baseItemType: string[] = []
    itemOptions.forEach((option) => {
      if (
        option.size &&
        option.size.name !== '' &&
        !baseSizes.map((s) => s.size_id).includes(option.size.size_id)
      ) {
        baseSizes.push(option.size)
      }
      if (
        option.color &&
        option.color.name !== '' &&
        !baseColors.map((c) => c.color_id).includes(option.color.color_id)
      ) {
        baseColors.push(option.color)
      }
      if (option.name !== '' && !baseItemType.includes(option.name)) {
        baseItemType.push(option.name)
      }
    })

    return (
      (baseItemType.length > 0 && itemOptions.some((o) => o.name === '')) ||
      (baseSizes.length > 0 && itemOptions.some((o) => !o.size)) ||
      (baseColors.length > 0 && itemOptions.some((o) => !o.color))
    )
  }, [itemOptions])

  const optionsHaveDifferentPrice = itemOptions.some(
    (o) => o.price !== itemOptions[0].price,
  )

  const optionsHavingImages =
    itemOptions.filter((o) => o.item_image_url && o.item_image_url !== '')
      .length === itemOptions.length

  if (hasDefectiveData) {
    return (
      <ItemOptionWithDefectiveData
        itemOptions={itemOptions}
        selectedOption={selectedOption}
        onChanged={onChanged}
      />
    )
  } else if (optionsHavingImages) {
    return (
      <SelectItemOptionsWithImages
        itemOptions={itemOptions}
        optionsHaveDifferentPrice={optionsHaveDifferentPrice}
        selectedOption={selectedOption}
        onChanged={onChanged}
      />
    )
  } else {
    return (
      <SelectItemOptionsIndividual
        itemOptions={itemOptions}
        optionsHaveDifferentPrice={optionsHaveDifferentPrice}
        selectedOptions={selectedOption}
        onChanged={onChanged}
      />
    )
  }
}

// optionsに不備がある場合の選択UI
const ItemOptionWithDefectiveData = ({
  itemOptions,
  selectedOption,
  onChanged,
}: {
  itemOptions: localItemOptionType[]
  selectedOption: localItemOptionType
  onChanged: (option: localItemOptionType) => void
}) => {
  return (
    <>
      <div className="mt-4 w-full">
        <div className="text-sm mr-4 font-semibold">オプション</div>
        <div className="flex items-center mt-2 flex-wrap">
          {itemOptions.map((o) => (
            <button
              key={o.custom_option_id}
              className={`${
                selectedOption.custom_option_id === o.custom_option_id
                  ? 'bg-black text-white'
                  : 'bg-white text-black border'
              } text-sm font-medium py-1 px-3 mr-2 rounded-full min-w-[60px] mt-2`}
              onClick={() => {
                onChanged(o)
              }}
            >
              <ItemOption option={o} />
            </button>
          ))}
        </div>
      </div>
    </>
  )
}

const SelectItemOptionsWithImages = ({
  itemOptions,
  optionsHaveDifferentPrice,
  selectedOption,
  onChanged,
}: {
  itemOptions: localItemOptionType[]
  optionsHaveDifferentPrice: boolean
  selectedOption: localItemOptionType
  onChanged: (option: localItemOptionType) => void
}) => {
  // オプション選択の中間データ。propsのselectedOptionsとの違いとして、nullableになっている
  const [selectedOptionNullable, setSelectedOptionNullable] =
    useState<localItemOptionType | null>(selectedOption)

  useEffect(() => {
    if (selectedOptionNullable) {
      onChanged(selectedOptionNullable)
    }
  }, [selectedOptionNullable])

  return (
    <IndividualOptionWithImage
      itemOptions={itemOptions}
      selectedOption={selectedOptionNullable}
      setSelectedOption={setSelectedOptionNullable}
      optionsHaveDifferentPrice={optionsHaveDifferentPrice}
    />
  )
}

// オプション＋サイズ＋カラーで選択
// todo: 現状では不完全な実装になっている。selectedOptionsが変わっても、内部ステートに影響しないため。
const SelectItemOptionsIndividual = ({
  itemOptions,
  optionsHaveDifferentPrice,
  selectedOptions,
  onChanged,
}: {
  itemOptions: localItemOptionType[]
  optionsHaveDifferentPrice: boolean
  selectedOptions: localItemOptionType
  onChanged: (options: localItemOptionType) => void
}) => {
  // オプション選択の中間データ。propsのselectedOptionsとの違いとして、nullableになっている
  const [selectedOptionNullable, setSelectedOptionNullable] =
    useState<localItemOptionType | null>(selectedOptions)

  useEffect(() => {
    if (selectedOptionNullable) {
      onChanged(selectedOptionNullable)
    }
  }, [selectedOptionNullable])

  return (
    <IndividualOption
      itemOptions={itemOptions}
      selectedOption={selectedOptionNullable}
      setSelectedOption={setSelectedOptionNullable}
      optionsHaveDifferentPrice={optionsHaveDifferentPrice}
    />
  )
}

const IndividualOption = ({
  itemOptions,
  selectedOption,
  setSelectedOption,
}: {
  itemOptions: localItemOptionType[]
  selectedOption: localItemOptionType | null
  setSelectedOption: Dispatch<SetStateAction<localItemOptionType | null>>
  optionsHaveDifferentPrice: boolean
}) => {
  const baseSizes: localSizeType[] = []
  const baseColors: localColorType[] = []
  const baseItemType: string[] = []
  itemOptions.forEach((option) => {
    if (
      option.size &&
      option.size.name !== '' &&
      !baseSizes.map((s) => s.size_id).includes(option.size.size_id)
    ) {
      baseSizes.push(option.size)
    }
    if (
      option.color &&
      option.color.name !== '' &&
      !baseColors.map((c) => c.color_id).includes(option.color.color_id)
    ) {
      baseColors.push(option.color)
    }
    if (option.name !== '' && !baseItemType.includes(option.name)) {
      baseItemType.push(option.name)
    }
  })
  const [selectableSizes, setSelectableSizes] = useState<localSizeType[]>(
    getUniqueArray(itemOptions.map((o) => o.size!)),
  )
  const [selectableColors, setSelectableColors] = useState<localColorType[]>(
    getUniqueArray(itemOptions.map((o) => o.color!)),
  )
  const [selectableItemTypes, setSelectableItemTypes] = useState<string[]>(
    getUniqueArray(itemOptions.map((o) => o.name)),
  )
  const [selectedSize, setSelectedSize] = useState<localSizeType | null>(
    selectedOption?.size ?? null,
  )
  const [selectedColor, setSelectedColor] = useState<localColorType | null>(
    selectedOption?.color ?? null,
  )
  const [selectedItemType, setSelectedItemType] = useState<string | null>(
    selectedOption?.name ?? null,
  )

  return (
    <div className="">
      <div className="flex items-center flex-start w-full">
        {selectedOption && selectedOption.discounted_price > 0 ? (
          <div
            className="text-2xl font-bold text-center"
            style={{ color: '#424242' }}
          >
            {selectedOption.discounted_price.toLocaleString()}{' '}
            <span className="text-sm font-light"> 円 (税込)</span>
            <span className="original-price-text line-through text-xs text-gray-500">
              {selectedOption!.price.toLocaleString()}
              <span className="price-yen font-light ml-2"> 円 (割引前)</span>
            </span>
          </div>
        ) : (
          <span className="normal-price text-xl font-bold my-2 text-[#424242]">
            {selectedOption?.price.toLocaleString()}{' '}
            <span className="text-sm font-light ml-2">円</span>
          </span>
        )}
      </div>
      <div className="my-2 w-full">
        {selectableItemTypes.length > 0 && baseItemType.length > 1 && (
          <>
            <div className="flex items-center mt-2 flex-wrap">
              {selectableItemTypes.map((type) => (
                <button
                  key={type}
                  className={`${
                    selectedItemType === type
                      ? 'bg-black text-white'
                      : 'bg-white text-black border'
                  } text-sm font-medium py-1 px-3 mr-2 rounded-full min-w-[60px] mt-2`}
                  onClick={() => {
                    const selectableOption = itemOptions.filter(
                      (o) => o.name === type,
                    )
                    setSelectedItemType(type)
                    if (selectedColor && selectedSize) {
                      const selectableColors = selectableOption.map(
                        (o) => o.color!,
                      )
                      const selectableSizes = selectableOption.map(
                        (o) => o.size!,
                      )
                      setSelectableColors(getUniqueArray(selectableColors))
                      setSelectableSizes(getUniqueArray(selectableSizes))
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.size!.size_id === selectedSize.size_id &&
                            o.name === type,
                        ) || null,
                      )
                    } else if (selectedColor && !selectedSize) {
                      setSelectableColors(
                        getUniqueArray(selectableOption.map((o) => o.color!)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.name === type,
                        ) || null,
                      )
                    } else if (!selectedColor && selectedSize) {
                      setSelectableSizes(
                        getUniqueArray(selectableOption.map((o) => o.size!)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.size!.size_id === selectedSize.size_id &&
                            o.name === type,
                        ) || null,
                      )
                    } else {
                      setSelectedOption(
                        itemOptions.find((o) => o.name === type) || null,
                      )
                    }
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </>
        )}
        <div className="mx-auto">
          {selectableSizes.length > 0 && baseSizes.length > 0 && (
            <div className="w-full mt-4">
              <div className="text-sm mr-4 font-semibold">サイズ</div>
              {sizeSort(selectableSizes).map((size) => (
                <button
                  key={size.size_id}
                  className={`${
                    selectedSize?.size_id === size.size_id
                      ? 'bg-black text-white'
                      : 'bg-white text-black border'
                  } text-sm font-medium py-1 px-3 mr-2 rounded-full min-w-[60px] mt-2`}
                  onClick={() => {
                    const selectableOption = itemOptions.filter(
                      (o) => o.size!.size_id === size.size_id,
                    )
                    setSelectedSize(size)
                    if (selectedColor && selectedItemType) {
                      const selectableColors = selectableOption.map(
                        (o) => o.color!,
                      )
                      const selectableItemTypes = selectableOption.map(
                        (o) => o.name,
                      )
                      setSelectableColors(getUniqueArray(selectableColors))
                      setSelectableItemTypes(
                        getUniqueArray(selectableItemTypes),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.size!.size_id === size.size_id &&
                            o.name === selectedItemType,
                        ) || null,
                      )
                    } else if (selectedColor && !selectedItemType) {
                      setSelectableColors(
                        getUniqueArray(selectableOption.map((o) => o.color!)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.size!.size_id === size.size_id,
                        ) || null,
                      )
                    } else if (!selectedColor && selectedItemType) {
                      setSelectableItemTypes(
                        getUniqueArray(selectableOption.map((o) => o.name)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.size!.size_id === size.size_id &&
                            o.name === selectedItemType,
                        ) || null,
                      )
                    } else {
                      setSelectedOption(
                        itemOptions.find(
                          (o) => o.size!.size_id === size.size_id,
                        ) || null,
                      )
                    }
                  }}
                >
                  {size.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="w-full mt-4">
          {selectableColors.length > 0 && baseColors.length > 0 && (
            <div className="w-full mt-4">
              <div className="text-sm mr-4 font-semibold">カラー</div>
              {colorSort(selectableColors).map((color) => (
                <button
                  key={color.color_id}
                  className={`${
                    selectedColor?.color_id === color.color_id
                      ? 'bg-black text-white'
                      : 'bg-white text-black border'
                  } text-sm font-medium py-1 px-3 mr-2 rounded-full min-w-[60px] mt-2`}
                  onClick={() => {
                    const selectableOption = itemOptions.filter(
                      (o) => o.color!.color_id === color.color_id,
                    )
                    setSelectedColor(color)
                    if (selectedSize && selectedItemType) {
                      const selectableSizes = selectableOption.map(
                        (o) => o.size!,
                      )
                      const selectableItemTypes = selectableOption.map(
                        (o) => o.name,
                      )
                      setSelectableSizes(getUniqueArray(selectableSizes))
                      setSelectableItemTypes(
                        getUniqueArray(selectableItemTypes),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === color.color_id &&
                            o.size!.size_id === selectedSize.size_id &&
                            o.name === selectedItemType,
                        ) || null,
                      )
                    } else if (selectedSize && !selectedItemType) {
                      setSelectableSizes(
                        getUniqueArray(selectableOption.map((o) => o.size!)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === color.color_id &&
                            o.size!.size_id === selectedSize.size_id,
                        ) || null,
                      )
                    } else if (!selectedSize && selectedItemType) {
                      setSelectableItemTypes(
                        getUniqueArray(selectableOption.map((o) => o.name)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === color.color_id &&
                            o.name === selectedItemType,
                        ) || null,
                      )
                    } else {
                      setSelectedOption(
                        itemOptions.find(
                          (o) => o.color!.color_id === color.color_id,
                        ) || null,
                      )
                    }
                  }}
                >
                  {color.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const IndividualOptionWithImage = ({
  itemOptions,
  selectedOption,
  setSelectedOption,
}: {
  itemOptions: localItemOptionType[]
  selectedOption: localItemOptionType | null
  setSelectedOption: Dispatch<SetStateAction<localItemOptionType | null>>
  optionsHaveDifferentPrice: boolean
}) => {
  const baseSizes: localSizeType[] = []
  const baseColors: localColorType[] = []
  const baseItemType: string[] = []
  itemOptions.forEach((option) => {
    if (
      option.size &&
      option.size.name !== '' &&
      !baseSizes.map((s) => s.size_id).includes(option.size.size_id)
    ) {
      baseSizes.push(option.size)
    }
    if (
      option.color &&
      option.color.name !== '' &&
      !baseColors.map((c) => c.color_id).includes(option.color.color_id)
    ) {
      baseColors.push(option.color)
    }
    if (option.name !== '' && !baseItemType.includes(option.name)) {
      baseItemType.push(option.name)
    }
  })
  const [selectableSizes, setSelectableSizes] = useState<localSizeType[]>(
    getUniqueArray(itemOptions.map((o) => o.size!)),
  )
  const [selectableColors, setSelectableColors] = useState<localColorType[]>(
    getUniqueArray(itemOptions.map((o) => o.color!)),
  )
  const [selectableItemTypes, setSelectableItemTypes] = useState<string[]>(
    getUniqueArray(itemOptions.map((o) => o.name)),
  )
  const [selectedSize, setSelectedSize] = useState<localSizeType | null>(
    selectedOption?.size ?? null,
  )
  const [selectedColor, setSelectedColor] = useState<localColorType | null>(
    selectedOption?.color ?? null,
  )
  const [selectedItemType, setSelectedItemType] = useState<string | null>(
    selectedOption?.name ?? null,
  )

  return (
    <div className="">
      <div className="flex items-center flex-start w-full">
        {selectedOption && selectedOption.discounted_price > 0 ? (
          <div
            className="text-2xl font-bold text-center"
            style={{ color: '#424242' }}
          >
            {selectedOption.discounted_price.toLocaleString()}{' '}
            <span className="text-sm font-light"> 円 (税込)</span>
            <span className="original-price-text line-through text-xs text-gray-500">
              {selectedOption.price.toLocaleString()}
              <span className="price-yen font-light ml-2"> 円 (割引前)</span>
            </span>
          </div>
        ) : (
          <span className="normal-price text-xl font-bold my-2 text-[#424242]">
            {selectedOption?.price.toLocaleString()}{' '}
            <span className="text-sm font-light ml-2">円</span>
          </span>
        )}
      </div>
      <div className="my-2 w-full">
        {selectableItemTypes.length > 0 && baseItemType.length > 1 && (
          <>
            <div className="flex items-center mt-2 flex-wrap hidden">
              {selectableItemTypes.map((type) => (
                <button
                  key={type}
                  className={`${
                    selectedItemType === type
                      ? 'bg-black text-white'
                      : 'bg-white text-black border'
                  } text-sm font-medium py-1 px-3 mr-2 rounded-full min-w-[60px] mt-2`}
                  onClick={() => {
                    const selectableOption = itemOptions.filter(
                      (o) => o.name === type,
                    )
                    setSelectedItemType(type)
                    if (selectedColor && selectedSize) {
                      const selectableColors = selectableOption.map(
                        (o) => o.color!,
                      )
                      const selectableSizes = selectableOption.map(
                        (o) => o.size!,
                      )
                      setSelectableColors(getUniqueArray(selectableColors))
                      setSelectableSizes(getUniqueArray(selectableSizes))
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.size!.size_id === selectedSize.size_id &&
                            o.name === type,
                        ) || null,
                      )
                    } else if (selectedColor && !selectedSize) {
                      setSelectableColors(
                        getUniqueArray(selectableOption.map((o) => o.color!)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.name === type,
                        ) || null,
                      )
                    } else if (!selectedColor && selectedSize) {
                      setSelectableSizes(
                        getUniqueArray(selectableOption.map((o) => o.size!)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.size!.size_id === selectedSize.size_id &&
                            o.name === type,
                        ) || null,
                      )
                    } else {
                      setSelectedOption(
                        itemOptions.find((o) => o.name === type) || null,
                      )
                    }
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </>
        )}
        <div className="w-full mt-4">
          {selectableColors.length > 0 && baseColors.length > 0 && (
            <div className="w-full mt-4">
              <div className="text-sm mr-4 font-semibold">
                カラー: {selectedColor?.name}
              </div>
              <div className="flex items-center mt-2 overflow-x-auto">
                {colorSort(selectableColors).map((color) => (
                  <button
                    key={color.color_id}
                    className={`
${
  selectedColor?.color_id === color.color_id ? 'border border-black' : 'border'
} inline-block my-2 mr-2  flex-shrink-0
                  `}
                    onClick={() => {
                      const selectableOption = itemOptions.filter(
                        (o) => o.color!.color_id === color.color_id,
                      )
                      setSelectedColor(color)
                      if (selectedSize && selectedItemType) {
                        const selectableSizes = selectableOption.map(
                          (o) => o.size!,
                        )
                        const selectableItemTypes = selectableOption.map(
                          (o) => o.name,
                        )
                        setSelectableSizes(getUniqueArray(selectableSizes))
                        setSelectableItemTypes(
                          getUniqueArray(selectableItemTypes),
                        )
                        setSelectedOption(
                          itemOptions.find(
                            (o) =>
                              o.color!.color_id === color.color_id &&
                              o.size!.size_id === selectedSize.size_id &&
                              o.name === selectedItemType,
                          ) || null,
                        )
                      } else if (selectedSize && !selectedItemType) {
                        setSelectableSizes(
                          getUniqueArray(selectableOption.map((o) => o.size!)),
                        )
                        setSelectedOption(
                          itemOptions.find(
                            (o) =>
                              o.color!.color_id === color.color_id &&
                              o.size!.size_id === selectedSize.size_id,
                          ) || null,
                        )
                      } else if (!selectedSize && selectedItemType) {
                        setSelectableItemTypes(
                          getUniqueArray(selectableOption.map((o) => o.name)),
                        )
                        setSelectedOption(
                          itemOptions.find(
                            (o) =>
                              o.color!.color_id === color.color_id &&
                              o.name === selectedItemType,
                          ) || null,
                        )
                      } else {
                        setSelectedOption(
                          itemOptions.find(
                            (o) => o.color!.color_id === color.color_id,
                          ) || null,
                        )
                      }
                    }}
                  >
                    <img
                      src={
                        itemOptions.find(
                          (o) => o.color!.color_id === color.color_id,
                        )?.item_image_url
                      }
                      alt={color.name}
                      className="w-16 h-16 object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mx-auto">
          {selectableSizes.length > 0 && baseSizes.length > 0 && (
            <div className="w-full mt-4">
              <div className="text-sm mr-4 font-semibold">サイズ</div>
              {sizeSort(selectableSizes).map((size) => (
                <button
                  key={size.size_id}
                  className={`${
                    selectedSize?.size_id === size.size_id
                      ? 'bg-black text-white'
                      : 'bg-white text-black border'
                  } text-sm font-medium py-1 px-3 mr-2 rounded-full min-w-[60px] mt-2`}
                  onClick={() => {
                    const selectableOption = itemOptions.filter(
                      (o) => o.size!.size_id === size.size_id,
                    )
                    setSelectedSize(size)
                    if (selectedColor && selectedItemType) {
                      const selectableColors = selectableOption.map(
                        (o) => o.color!,
                      )
                      const selectableItemTypes = selectableOption.map(
                        (o) => o.name,
                      )
                      setSelectableColors(getUniqueArray(selectableColors))
                      setSelectableItemTypes(
                        getUniqueArray(selectableItemTypes),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.size!.size_id === size.size_id &&
                            o.name === selectedItemType,
                        ) || null,
                      )
                    } else if (selectedColor && !selectedItemType) {
                      setSelectableColors(
                        getUniqueArray(selectableOption.map((o) => o.color!)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.size!.size_id === size.size_id,
                        ) || null,
                      )
                    } else if (!selectedColor && selectedItemType) {
                      setSelectableItemTypes(
                        getUniqueArray(selectableOption.map((o) => o.name)),
                      )
                      setSelectedOption(
                        itemOptions.find(
                          (o) =>
                            o.size!.size_id === size.size_id &&
                            o.name === selectedItemType,
                        ) || null,
                      )
                    } else {
                      setSelectedOption(
                        itemOptions.find(
                          (o) => o.size!.size_id === size.size_id,
                        ) || null,
                      )
                    }
                  }}
                >
                  {size.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const getUniqueArray = (options: any[]) => {
  return Array.from(
    new Set(
      options
        .filter((item) => item !== undefined) // undefined を除外
        .map((item) => {
          try {
            return JSON.stringify(item)
          } catch (e) {
            // JSON.stringify できない値の場合はスキップ
            return ''
          }
        })
        .filter((item) => item !== null), // null を除外
    ),
  ).map((item) => JSON.parse(item))
}
